/**
 * GuestDetailModal — Gastdetails mit Gästekarten und Buchungen.
 *
 * Extrahiert aus `pages/GuestCardManagement.tsx`, damit die Gästekarten-Verwaltung
 * und das Vorstands-Dashboard dieselbe Ansicht und dieselben Aktionen nutzen:
 * Karte als bezahlt markieren (mit Datum und optionalem Beleg) und Buchungen
 * stornieren (24-Stunden-Regel).
 *
 * Karten und Buchungen lädt das Modal selbst nach — Aufrufer müssen nur den Gast
 * benennen (`mg_nr`, Vor- und Nachname).
 */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  CalendarIcon,
  CheckCircleIcon,
  CurrencyEuroIcon,
  PhoneIcon,
  TicketIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../App';
import apiClient from '../../utils/apiClient';
import { ModalShell, ModalActions, ModalSection } from './core';
import ConfirmationModal from '../ConfirmationModal';
import AttributeLabel from '../AttributeLabel';
import GuestBookingCard from '../GuestBookingCard';
import {
  getCancellationStatus,
  guestInitials,
  type GuestCard,
  type GuestRef,
} from '../../types/guest';

interface ConfirmState {
  isOpen: boolean;
  type: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant: 'single' | 'two';
  onConfirm?: () => void;
}

export interface GuestDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** Gast, dessen Details gezeigt werden. */
  guest: GuestRef | null;
  /** Nach Zahlung oder Storno aufrufen, damit der Aufrufer neu laden kann. */
  onChanged?: () => void;
}

const GuestDetailModal: React.FC<GuestDetailModalProps> = ({
  open,
  onClose,
  guest,
  onChanged,
}) => {
  const { BASE_URL } = useContext(AuthContext);
  const [cards, setCards] = useState<GuestCard[]>([]);
  const [hasGuestCardAccount, setHasGuestCardAccount] = useState<boolean | null>(null);
  const [selectedCard, setSelectedCard] = useState<GuestCard | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<{ bezahlt_am: string; document: File | null }>({
    bezahlt_am: new Date().toISOString().split('T')[0],
    document: null,
  });
  const [confirm, setConfirm] = useState<ConfirmState>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single',
  });
  const paymentFormRef = useRef<HTMLFormElement>(null);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-DE');
  };

  /** Profilbild des Gasts (nur wenn ein Foto hinterlegt ist). */
  const getGuestImageUrl = (g: GuestRef): string | undefined => {
    if (!g.foto_path) return undefined;
    const token = localStorage.getItem('jwt_token');
    return `${BASE_URL}/api/members/user-image-url/${g.mg_nr}?size=S&token=${token}&ts=${g.foto_path_updated || ''}`;
  };

  /** Karten samt Buchungen laden. */
  const loadCards = useCallback(async (mgNr: string) => {
    try {
      const res = await apiClient.get(`/api/guest-cards/guest-cards?member_id=${mgNr}`);
      if (res.data?.success) {
        const loaded = (res.data.cards || []).map((card: any) => ({
          ...card,
          // Die API liefert je nach Pfad `name` oder `card_name`.
          card_name: card.card_name || card.name || card.type?.name || 'Gästekarte',
        }));
        setCards(loaded);
      } else {
        setCards([]);
      }
    } catch (error) {
      console.error('GuestDetailModal: Karten konnten nicht geladen werden', error);
      setCards([]);
    }
  }, []);

  /** Prüfen, ob ein Gästekarten-Konto konfiguriert ist (Voraussetzung für die Zahlung). */
  const loadAccountFlag = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/finance/accounts');
      const accounts = res.data || [];
      setHasGuestCardAccount(accounts.some((acc: any) => acc.ist_gaestekarten_konto === 1));
    } catch (error) {
      console.error('GuestDetailModal: Konten konnten nicht geladen werden', error);
      setHasGuestCardAccount(false);
    }
  }, []);

  useEffect(() => {
    if (open && guest?.mg_nr) {
      loadCards(guest.mg_nr);
      loadAccountFlag();
    }
    if (!open) setCards([]);
  }, [open, guest?.mg_nr, loadCards, loadAccountFlag]);

  /** Karte als bezahlt markieren (Datum, optionaler Beleg). */
  const handleMarkPaid = async () => {
    if (!selectedCard) return;
    try {
      const formData = new FormData();
      formData.append('card_id', selectedCard.id.toString());
      formData.append('bezahlt_am', paymentDetails.bezahlt_am);
      if (paymentDetails.document) formData.append('document', paymentDetails.document);

      const res = await apiClient.post('/api/guest-cards/mark-paid', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data?.success) {
        if (guest?.mg_nr) await loadCards(guest.mg_nr);
        onChanged?.();
        setShowPaymentModal(false);
        setSelectedCard(null);
        setPaymentDetails({ bezahlt_am: new Date().toISOString().split('T')[0], document: null });
        setConfirm({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Die Karte wurde als bezahlt markiert.',
          variant: 'single',
        });
      }
    } catch (error) {
      console.error('GuestDetailModal: Zahlung fehlgeschlagen', error);
      setConfirm({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Die Karte konnte nicht als bezahlt markiert werden.',
        variant: 'single',
      });
    }
  };

  /** Buchung stornieren — erst 24-Stunden-Regel prüfen, dann bestätigen lassen. */
  const handleCancelBooking = (booking: any) => {
    const status = getCancellationStatus(booking);
    if (!status.allowed) {
      setConfirm({
        isOpen: true,
        type: 'warning',
        title: 'Stornierung nicht möglich',
        message: status.reason || 'Diese Buchung kann nicht mehr storniert werden.',
        variant: 'single',
      });
      return;
    }

    setConfirm({
      isOpen: true,
      type: 'warning',
      title: 'Buchung stornieren',
      message: `Möchten Sie die Buchung für „${booking.title}" wirklich stornieren?`,
      variant: 'two',
      onConfirm: async () => {
        const memberId = guest?.mg_nr;
        const cardId = booking.card_id || booking.guest_card_id || cards[0]?.id;
        if (!memberId || !cardId || !booking.kalender_id) {
          setConfirm({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Fehlende Daten für die Stornierung. Bitte versuchen Sie es später erneut.',
            variant: 'single',
          });
          return;
        }
        try {
          await apiClient.delete(
            `/api/members/cancel-event/${booking.kalender_id}?member_id=${memberId}&card_id=${cardId}`,
          );
          await loadCards(memberId);
          onChanged?.();
          setConfirm({
            isOpen: true,
            type: 'success',
            title: 'Erfolgreich',
            message: 'Buchung wurde erfolgreich storniert.',
            variant: 'single',
          });
        } catch (error) {
          console.error('GuestDetailModal: Storno fehlgeschlagen', error);
          setConfirm({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Fehler beim Stornieren der Buchung.',
            variant: 'single',
          });
        }
      },
    });
  };

  return (
    <>
      {guest && (
        <ModalShell
          open={open}
          onClose={() => onClose()}
          variant="detail"
          size="md"
          mobilePresentation="sheet"
          accent="brand"
          icon={UserCircleIcon}
          avatarUrl={getGuestImageUrl(guest)}
          avatarInitials={guestInitials(guest)}
          title={`${guest.vorname} ${guest.name}`}
          subtitle={
            <>
              Gastmitglied
              {guest.eintritt ? ` · seit ${new Date(guest.eintritt).getFullYear()}` : ''}
              {' · Aktiv'}
            </>
          }
          bodyClassName="bg-gray-50/60 dark:bg-gray-950/20"
          footer={
            <ModalActions
              secondary={{ label: 'Schließen', onClick: () => onClose() }}
            />
          }
        >
          {/* Kontakt und Aktivität als kompakte Übersicht */}
          <ModalSection className="mb-5">
            <div className="mb-2.5 flex items-center gap-2">
              <UserCircleIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Kontakt & Aktivität</h3>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {guest.tel_nummer && (
                <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-3.5 dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                      <PhoneIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Telefon</p>
                      <a href={`tel:${guest.tel_nummer}`} className="text-sm font-semibold text-primary-700 hover:underline dark:text-primary-300">{guest.tel_nummer}</a>
                    </div>
                  </div>
                </div>
              )}
              <div className="rounded-xl border border-gray-200 bg-white p-3.5 dark:border-gray-700 dark:bg-gray-800">
                <AttributeLabel iconKey="time" size="sm" textClassName="text-[11px] font-bold uppercase tracking-wide text-gray-400">Letzter Login</AttributeLabel>
                <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">{guest.last_login ? formatDate(guest.last_login) : 'Noch kein Login'}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-3.5 dark:border-gray-700 dark:bg-gray-800">
                <AttributeLabel iconKey="booking" size="sm" textClassName="text-[11px] font-bold uppercase tracking-wide text-gray-400">Letzte Buchung</AttributeLabel>
                <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">{guest.last_booking ? formatDate(guest.last_booking) : 'Noch keine Buchung'}</p>
              </div>
            </div>
          </ModalSection>

          {/* Gästekarten */}
          <ModalSection className="mb-5">
            <div className="mb-2.5 flex items-center gap-2">
              <TicketIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Gästekarten</h3>
              <span className="ml-auto rounded-full bg-gray-200 px-2 py-0.5 text-xs font-bold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {cards.length}
              </span>
            </div>

            {/* Cards Content */}
            {cards.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {cards.map((card) => {
                  const isExpired = card.expiry_date && !isNaN(new Date(card.expiry_date).getTime()) && new Date(card.expiry_date) < new Date();
                  const hasEntries = card.remaining_entries > 0;
                  const isUnpaid = !card.bezahlt && !card.bezahlt_am;
                  const totalEntries = card.total_entries || card.type?.total_entries || 0;

                  return (
                    <div
                      key={card.id}
                      className={`relative overflow-hidden rounded-xl border bg-white shadow-sm transition-colors dark:bg-gray-800 ${
                        isExpired
                          ? 'border-error-200 dark:border-error-800'
                          : isUnpaid
                            ? 'border-amber-200 dark:border-amber-800'
                            : hasEntries
                              ? 'border-success-200 dark:border-success-800'
                              : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="p-3.5">
                        <div className="flex items-center gap-3">
                          {/* Icon */}
                          <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
                            isExpired
                              ? 'bg-error-100 dark:bg-error-900/40'
                              : hasEntries
                                ? 'bg-success-100 dark:bg-success-900/40'
                                : 'bg-gray-100 dark:bg-gray-700'
                          }`}>
                            <TicketIcon className={`h-5 w-5 ${
                              isExpired
                                ? 'text-error-600 dark:text-error-400'
                                : hasEntries
                                  ? 'text-success-600 dark:text-success-400'
                                  : 'text-gray-500 dark:text-gray-400'
                            }`} />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                              {card.card_name || card.type?.name || 'Gästekarte'}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              #{card.id} · {formatDate(card.purchase_date)}
                            </p>
                          </div>

                          {/* Entries Counter */}
                          <div className="flex-shrink-0 rounded-lg bg-gray-50 px-2.5 py-1.5 text-right dark:bg-gray-700/60">
                            <p className={`text-lg font-bold leading-none ${
                              card.remaining_entries === 0
                                ? 'text-error-600 dark:text-error-400'
                                : 'text-gray-900 dark:text-white'
                            }`}>
                              {card.remaining_entries}
                            </p>
                            <p className="mt-0.5 text-[10px] font-medium text-gray-400">von {totalEntries || '?'}</p>
                          </div>
                        </div>

                        {/* Status Badges */}
                        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-3 dark:border-gray-700">
                          {isExpired && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-error-100 dark:bg-error-900/40 text-error-700 dark:text-error-400 rounded-md">
                              Abgelaufen
                            </span>
                          )}
                          {!hasEntries && !isExpired && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-warning-100 dark:bg-warning-900/40 text-warning-700 dark:text-warning-400 rounded-md">
                              Aufgebraucht
                            </span>
                          )}
                          {(card.bezahlt || card.bezahlt_am) ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-success-100 dark:bg-success-900/40 text-success-700 dark:text-success-400 rounded-md">
                              <CheckCircleIcon className="h-3 w-3" />
                              Bezahlt
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded-md">
                              Offen
                            </span>
                          )}
                          {card.expiry_date && !isExpired && (
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">
                              bis {formatDate(card.expiry_date)}
                            </span>
                          )}
                        </div>

                        {/* Action Button */}
                        {!card.bezahlt && !card.bezahlt_am && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <button
                              onClick={() => {
                                if (hasGuestCardAccount) {
                                  setSelectedCard(card);
                                  setShowPaymentModal(true);
                                }
                              }}
                              disabled={!hasGuestCardAccount}
                              className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl font-semibold transition-colors text-sm ${
                                hasGuestCardAccount
                                  ? 'bg-success-600 hover:bg-success-700 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                              }`}
                              title={hasGuestCardAccount ? "Als bezahlt markieren" : "Kein Gästekarten-Konto konfiguriert"}
                            >
                              <CurrencyEuroIcon className="h-4 w-4" />
                              Als bezahlt markieren
                            </button>
                            {!hasGuestCardAccount && (
                              <p className="mt-2 text-xs text-error-500 dark:text-error-400 text-center">
                                Kein Gästekarten-Konto konfiguriert
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Empty State */
              <div className="rounded-xl border border-dashed border-gray-300 bg-white py-7 text-center dark:border-gray-700 dark:bg-gray-800">
                <TicketIcon className="mx-auto mb-2 h-8 w-8 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Keine Gästekarten vorhanden</p>
              </div>
            )}
          </ModalSection>

          {/* Buchungen */}
          <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            {/* Section Header */}
            <div className="flex items-center gap-2.5 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <CalendarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-bold text-gray-900 dark:text-white">Buchungen</span>
              {cards && (
                <span className="ml-auto bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full text-xs font-semibold text-gray-500 dark:text-gray-400">
                  {cards.reduce((acc: number, card: any) => acc + (card.bookings?.length || 0), 0)}
                </span>
              )}
            </div>

            {/* Bookings Content */}
            {cards && cards.some((card: any) => card.bookings && card.bookings.length > 0) ? (
              <div className="p-4 space-y-4">
                {cards.map((card: any) => (
                  card.bookings && card.bookings.length > 0 ? (
                    <div key={card.id} className="space-y-3">
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {card.name} (#{card.id})
                      </h4>
                      {card.bookings.map((booking: any, index: number) => {
                        const eventCardData = {
                          id: booking.kalender_id || booking.id || index,
                          title: booking.title,
                          start: booking.start,
                          start_time: booking.start_time,
                          end_time: booking.end_time || '',
                          creator_name: booking.creator_name || 'Trainer',
                          creator: booking.creator_id || '',
                          creator_image: booking.creator_image,
                          creator_image_updated: booking.creator_image_updated,
                          cotrainer: booking.cotrainer_id || '',
                          cotrainer_name: booking.cotrainer_name || '',
                          cotrainer_image: booking.cotrainer_image,
                          cotrainer_image_updated: booking.cotrainer_image_updated,
                          participantCount: booking.participantCount || 0,
                          max_teilnehmer: booking.max_teilnehmer,
                          farbe: booking.farbe,
                          unique_id: booking.unique_id,
                          booking_date: booking.booking_date,
                          cardName: booking.cardName || card.name,
                          cardId: booking.cardId || card.id,
                          kalender_id: booking.kalender_id
                        };

                        const cancellationStatus = getCancellationStatus(booking);

                        return (
                          <GuestBookingCard
                            key={`${card.id}-${booking.termin_booking_id || booking.kalender_id || booking.id || index}`}
                            event={eventCardData}
                            showCancelButton={true}
                            onCancel={() => handleCancelBooking(booking)}
                            BASE_URL={BASE_URL}
                            cancellationStatus={cancellationStatus}
                          />
                        );
                      })}
                    </div>
                  ) : null
                ))}
              </div>
            ) : (
              /* Empty State */
              <div className="py-8 text-center">
                <CalendarIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm text-gray-400 dark:text-gray-500">Keine Buchungen vorhanden</p>
              </div>
            )}
          </section>
        </ModalShell>
      )}


      {selectedCard && (
        <ModalShell
          open={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedCard(null);
          }}
          variant="form"
          size="sm"
          title="Als bezahlt markieren"
          icon={CurrencyEuroIcon}
          closeOnBackdrop={false}
          footer={
            <ModalActions
              secondary={{
                label: 'Abbrechen',
                onClick: () => {
                  setShowPaymentModal(false);
                  setSelectedCard(null);
                },
              }}
              primary={{
                label: 'Als bezahlt markieren',
                onClick: () => paymentFormRef.current?.requestSubmit(),
              }}
            />
          }
        >
          <form ref={paymentFormRef} onSubmit={(e) => { e.preventDefault(); handleMarkPaid(); }} className="space-y-4">
            <div>
              <AttributeLabel iconKey="date" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Zahlungsdatum
              </AttributeLabel>
              <input
                type="date"
                required
                value={paymentDetails.bezahlt_am}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, bezahlt_am: e.target.value })}
                className="w-full bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <AttributeLabel iconKey="receipt" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Beleg (optional)
              </AttributeLabel>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setPaymentDetails({
                  ...paymentDetails,
                  document: e.target.files ? e.target.files[0] : null
                })}
                className="w-full bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl px-4 py-3 text-sm text-gray-900 dark:text-white file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-100 file:text-primary-700 dark:file:bg-primary-900/30 dark:file:text-primary-400 hover:file:bg-primary-200 focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </form>
        </ModalShell>
      )}


      <ConfirmationModal
        visible={confirm.isOpen}
        onClose={() => setConfirm({ ...confirm, isOpen: false })}
        type={confirm.type}
        destructive={confirm.variant === 'two' && /stornier/i.test(confirm.title || '')}
        title={confirm.title}
        message={confirm.message}
        variant={confirm.variant}
        showCloseButton
        button={
          confirm.variant === 'single'
            ? { text: 'OK', onPress: () => setConfirm({ ...confirm, isOpen: false }) }
            : undefined
        }
        buttons={
          confirm.variant === 'two'
            ? {
                cancel: { text: 'Abbrechen', onPress: () => setConfirm({ ...confirm, isOpen: false }) },
                confirm: {
                  text: 'Bestätigen',
                  onPress: () => {
                    confirm.onConfirm?.();
                    setConfirm({ ...confirm, isOpen: false });
                  },
                },
              }
            : undefined
        }
      />
    </>
  );
};

export default GuestDetailModal;
